import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, Building2 } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import {
  Stat,
  ProgressBar,
  Tabs,
  Pill,
  EntityStatusPill,
  Briefing,
  RightRail,
  RailSection,
  ActivityFeed,
  eventToActivityItem,
  EmptyState,
  MilestoneStatusPill,
  Button,
} from "@/design/components";
import { useSeed, useAuditEvents, useSubProject, useProject, useOrgIndex, useMilestones, useTerminology, useMoneyParts, useMoney, useActiveProfile, useProjectMembers } from "@/domain/hooks";
import { roleLabelFor } from "@/domain/cascadeRoles";
import { deriveBriefingAlerts, railSeverityOfAlerts } from "@/screens/shared/briefing";
import { ApprovalQueue } from "@/screens/approval/ApprovalQueue";
import { AnalyticsSurface } from "@/screens/analytics/AnalyticsSurface";
import { AuditTrail } from "@/screens/audit/AuditTrail";
import { AssignLeadContractorModal } from "@/components/AssignLeadContractorModal";

export function SubProjectView() {
  const { workspaceId, projectId = "", subProjectId = "" } = useParams();
  const seed = useSeed();
  const sub = useSubProject(subProjectId);
  const project = useProject(projectId);
  const mp = useMoneyParts();
  const money = useMoney();
  const orgs = useOrgIndex();
  const milestones = useMilestones(projectId);
  const term = useTerminology();
  const profile = useActiveProfile();
  const [tab, setTab] = useState("approvals");
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [assignLeadOpen, setAssignLeadOpen] = useState(false);
  const subProjectEvents = useAuditEvents({ subProjectId });

  if (!sub) {
    return (
      <WorkspaceShell>
        <div className="p-8"><EmptyState title="Sub-project not found" body="This sub-project is not in the active workspace seed." /></div>
      </WorkspaceShell>
    );
  }

  const lead = orgs[sub.leadContractorId];
  // Sub-projects participate in 2-3 milestones; show inline pills.
  const subMilestones = milestones.filter((m) => ["ms-frame", "ms-roofing", "ms-interior"].includes(m.id));

  const briefingAlerts = deriveBriefingAlerts(seed, { subProjectIds: [subProjectId] }, money, (kind, label) => ({
    label,
    onClick: () => setTab(kind === "evidence" ? "evidence" : kind === "findings" ? "analytics" : "approvals"),
  }));
  const lastEventAt = subProjectEvents[0]?.timestamp;

  return (
    <WorkspaceShell
      rightRail={
        <RightRail
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
          summary={[{ id: "briefing", count: briefingAlerts.length, severity: railSeverityOfAlerts(briefingAlerts) }]}
        >
          {/* The briefing is this surface's only attention queue, so it leads as the action zone. */}
          <RailSection
            title="Briefing"
            tone="action"
            icon={AlertTriangle}
            count={briefingAlerts.length}
            severity={railSeverityOfAlerts(briefingAlerts)}
          >
            <Briefing alerts={briefingAlerts} updatedAt={lastEventAt} />
          </RailSection>
          <RailSection title="Activity" divider>
            <ActivityFeed density="compact" items={subProjectEvents.slice(0, 6).map((e) => eventToActivityItem(e, { meta: orgs[e.actorOrganisationId]?.name, amount: e.amount ? money(e.amount) : undefined }))} />
          </RailSection>
        </RightRail>
      }
    >
      <div className="px-4 py-5 md:px-8 md:py-6">
        <div className="text-sm text-mute">
          <Link to={`/workspace/${workspaceId}/projects/${projectId}`} className="hover:text-ink">{project?.name}</Link>
          <span className="mx-1.5 text-faint">/</span>
          <span className="text-ink">{sub.name}</span>
        </div>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-ink">{sub.name}</h1>
          <EntityStatusPill status={sub.status} />
        </div>
        <p className="mt-1 text-sm text-mute">Lead organisation: {lead?.name}</p>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Budgeted" value={mp(sub.budgeted).value} unit={mp(sub.budgeted).unit} />
          <Stat label="Spent" value={mp(sub.spent).value} unit={mp(sub.spent).unit} trend={<ProgressBar value={sub.budgeted ? sub.spent / sub.budgeted : 0} />} />
          <Stat label="Evidenced" value={mp(sub.evidenced).value} unit={mp(sub.evidenced).unit} trend={<ProgressBar value={sub.evidenced / Math.max(1, sub.spent)} tone="ok" />} />
          <Stat label="Contingency" value={mp(sub.budgeted * 0.1).value} unit={mp(sub.budgeted * 0.1).unit} sub="10% of budget" />
        </div>

        <div className="mt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-faint">Milestones</h2>
          <div className="flex flex-wrap gap-2">
            {subMilestones.map((m) => (
              <span key={m.id} className="flex items-center gap-2 rounded-md border border-line bg-card px-3 py-1.5 text-sm">
                <span className="text-ink">{m.name}</span>
                <MilestoneStatusPill status={m.status} />
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <Tabs
            items={[
              { value: "approvals", label: "Approvals" },
              { value: "evidence", label: "Evidence" },
              { value: "audit", label: "Audit trail" },
              { value: "team", label: "Team" },
              { value: "analytics", label: "Analytics" },
            ]}
            value={tab}
            onValueChange={setTab}
          />
        </div>

        <div className="mt-6">
          {tab === "approvals" && <ApprovalQueue audience="Subcontractor lead" scope={{ subProjectId }} />}
          {tab === "evidence" && <EmptyState title="No evidence packs yet" body="Evidence appears here once spend is committed and photographed against this sub-project." />}
          {tab === "audit" && <AuditTrail events={subProjectEvents} />}
          {tab === "team" && (
            <>
              {profile.group === "oversight" && (
                <div className="mb-3 flex justify-end">
                  <Button size="sm" variant="secondary" onClick={() => setAssignLeadOpen(true)}><Building2 className="h-4 w-4" /> Assign lead contractor</Button>
                </div>
              )}
              <AssignLeadContractorModal open={assignLeadOpen} onOpenChange={setAssignLeadOpen} projectId={sub.id} projectName={sub.name} />
              <SubTeam subProjectId={subProjectId} orgName={lead?.name} />
            </>
          )}
          {tab === "analytics" && <AnalyticsSurface scopeLabel={sub.name} />}
        </div>
      </div>
    </WorkspaceShell>
  );
}

function SubTeam({ subProjectId, orgName }: { subProjectId: string; orgName?: string }) {
  const { members: people, isLoading } = useProjectMembers(subProjectId);
  return (
    <div>
      <p className="mb-3 text-sm text-mute">{orgName ?? "This sub-project's"} team members on this sub-project.</p>
      {isLoading ? (
        <p className="text-sm text-faint">Loading members…</p>
      ) : people.length === 0 ? (
        <p className="rounded-md border border-line bg-subtle px-3 py-3 text-sm text-mute">
          No one has been added to this sub-project yet.
        </p>
      ) : (
        <ul className="divide-y divide-hairline rounded-lg border border-line bg-card">
          {people.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-ink">{p.name}</span>
              <span className="flex items-center gap-2">
                <span className="text-sm text-mute">{roleLabelFor(p.role)}</span>
                <Pill variant={p.status === "active" ? "success" : p.status === "pending" ? "warn" : "danger"}>{p.status}</Pill>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
